import React, { useState } from 'react'

import { IonList } from '@ionic/react'
import { useParams } from 'react-router'
import { motion } from 'framer-motion'
import clsx from 'clsx'

// Tabs
import ClinicNOtherEventRegisterConfirmationTab from '../clinicAndOther/Tabs/ClinicNOtherEventRegisterConfirmationTab'
import ClinicNOtherRegisterTab from '../clinicAndOther/Tabs/ClinicNOtherRegisterTab'
import ClinicNOtherStatusTab from '../clinicAndOther/Tabs/ClinicNOtherStatusTab'
import CompetitorEventRegisterFeesTab from '../clinicAndOther/Tabs/CompetitorEventRegisterFeesTab'
import CompetitorEventRegisterSignTab from '../competitorEventRegister/Tabs/CompetitorEventRegisterSignTab'
import CompetitorEventsRegisterPaperworkTab from '../competitorEventRegister/Tabs/CompetitorEventsRegisterPaperworkTab'
import ClinicNOtherRegisterHorseTab from '../clinicAndOther/Tabs/ClinicNOtherHorseTab'
import CompetitorEventRegisterTicketsTab from '../clinicAndOther/Tabs/CompetitorEventRegisterTicketsTab'
import { CompetitorEventRegisterComments } from '../competitorEventRegister/Tabs/CompetitorEventRegisterComments'
import LightboxCommonComponent from '../../../components/common/dialogs/full-screen/LightboxCommonComponent'
import { RegistrationButtons } from './components/RegistrationButtons'
import ViewsLoader from '../../../components/loader/ViewsLoader'

// Types
import { ILightboxProps, IRegistrationEventTab } from '../../../types/competitor_types'

import ITypes from './hooks/useEventRegistrationTabs.types'

// Redux
import { selectedEvent } from '../../../store/events/eventsSlice'
import { useAppSelector } from '../../../store/hooks'
import {
  selectFilteredPaperworkDocuments,
  selectRegistration,
  selectRegistrationErrors,
  selectRegistrationLoading,
} from '../../../store/registration/registrationSlice'
import { selectIsLoggedIn } from '../../../store/user/userSlice'
import useEventRegistrationTabs from './hooks/useEventRegistrationTabs/useEventRegistrationTabs'

// Constants
import { CONST } from '../../../const/const'
import { EVENT_REG_CLINIC_TABS_CONST, UI_REGISTER_CONST } from '../../../const/ui/ui-register.const'

type EventRegistrationTabsProps = {
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
}

/**
 *
 * @param props EventRegistrationTabsProps
 * @returns ReactJsxElement
 */
const EventRegistrationTabs = (props: EventRegistrationTabsProps) => {
  const { createRegistration } = useEventRegistrationTabs()

  const { eventId, userId } = useParams<ITypes['IParams']>()

  const registration = useAppSelector(selectRegistration)
  const filteredPaperworkDocuments = useAppSelector(selectFilteredPaperworkDocuments)
  const registrationLoading = useAppSelector(selectRegistrationLoading)
  const event = useAppSelector(selectedEvent)

  const [lightBox, setLightBox] = useState<ILightboxProps>({
    visible: false,
    slides: [],
  })
  const [eventTab, setEventTab] = useState<IRegistrationEventTab>({
    tab: CONST.UI.REGISTER.TABS.TEAM,
    step: 0,
  })
  const [isStepLoading, setIsStepLoading] = useState(false)
  const [isSplitBill, setIsSplitBill] = useState(false)

  const isLoggedIn = useAppSelector(selectIsLoggedIn)

  const registrationHasErrors = useAppSelector(selectRegistrationErrors)

  // Handles lightbox
  const handleLightBox = (props: ILightboxProps) => {
    setLightBox({
      ...props,
    })
  }

  // Closes lightbox
  const onLightBoxClose = () => {
    handleLightBox({ visible: false, slides: [] })
  }

  const setNavigateTab = (data: any) => {
    setEventTab(data)
  }

  if (!isLoggedIn) return null

  const tabs = !event?.Event?.questions?.[0]
    ? EVENT_REG_CLINIC_TABS_CONST.filter((tab) => tab.tabName !== UI_REGISTER_CONST.TABS.COMMENTS)
    : EVENT_REG_CLINIC_TABS_CONST

  return (
    <>
      <ul className="hidden md:flex flex-wrap">
        {tabs.map((tab, index) => {
          return (
            <button
              key={index}
              className={clsx(
                `text-nr inline-flex items-center text-SeabiscuitDark200ThemeColor mr-2 bg-transparent py-2 px-4 rounded-full mb-2 !border-none !ring-0 cursor-default`,
                eventTab.tab === tab.tabName &&
                  '!bg-SeabiscuitWhiteThemeColor text-SeabiscuitMainThemeColor',
                { 'opacity-50': !registration?.id }
              )}
            >
              {eventTab.tab === tab.tabName ? (
                <img className="mr-2" src={tab.activeIcon} alt={tab.tabName} />
              ) : (
                <img className="mr-2" src={tab.icon} alt={tab.tabName} />
              )}
              {tab.tabName}
            </button>
          )
        })}
      </ul>

      <IonList className="home_root_ion_list flex flex-col">
        <RegistrationButtons
          eventTab={eventTab}
          createRegistration={createRegistration}
          setEventTab={setEventTab}
          isStepLoading={isStepLoading}
          setIsStepLoading={setIsStepLoading}
          tabs={tabs}
          filteredPaperworkDocuments={filteredPaperworkDocuments}
          registrationHasErrors={registrationHasErrors}
          isSplitBill={isSplitBill}
        />
        {isStepLoading || registrationLoading ? (
          <ViewsLoader
            className="flex items-center w-full justify-center flex-col flex-1"
            size="xl"
            color="red"
          />
        ) : (
          <>
            {eventTab.tab === CONST.UI.REGISTER.TABS.TEAM && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <ClinicNOtherStatusTab
                  title="Add people & horses to start"
                  description="Riders, trainers, owners, guardians, etc."
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.HORSES && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <ClinicNOtherRegisterHorseTab
                  title="Horses"
                  description="Match horses to riders, per entry"
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.REGISTER && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <ClinicNOtherRegisterTab
                  handleModal={props.handleModal}
                  title="Register"
                  description="Add riders to classes to enter. You can add more than one rider per class."
                  eventId={eventId}
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.FEES && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <CompetitorEventRegisterFeesTab
                  title="Fees"
                  description="Add items to your registration"
                  eventId={eventId}
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.TICKETS && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <CompetitorEventRegisterTicketsTab
                  title="Tickets"
                  description="Add spectator tickets your order"
                  eventId={eventId}
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.PAPERWORK && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <CompetitorEventsRegisterPaperworkTab
                  title="Other Roles"
                  description="Add trainers, owners, guardians, etc. to your entries"
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.SIGN && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <CompetitorEventRegisterSignTab
                  title="Your Paperwork"
                  handleLightBox={handleLightBox}
                  description="Review and sign your paperwork to participate in this event"
                  userId={userId}
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.COMMENTS && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <CompetitorEventRegisterComments
                  event={event?.Event}
                  comments={registration?.answers || []}
                />
              </motion.div>
            )}

            {eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION && (
              <motion.div
                className="w-full mx-auto flex flex-col flex-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.1,
                }}
              >
                <ClinicNOtherEventRegisterConfirmationTab
                  title="Confirm and Pay"
                  activeTab={eventTab}
                  setEventTab={setNavigateTab}
                  description="Review and confirm your registation details"
                  setIsSplitBill={setIsSplitBill}
                  isSplitBill={isSplitBill}
                />
              </motion.div>
            )}
          </>
        )}
      </IonList>

      {/* -----------LightBox----------- */}
      <LightboxCommonComponent
        visible={lightBox.visible}
        onClose={onLightBoxClose}
        slides={lightBox.slides}
      />
    </>
  )
}

export default EventRegistrationTabs
